import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Trash2, Plus, Minus, ArrowLeft, Lock, Tag, Truck } from "lucide-react";
import { useCart } from "../components/CartContext";
import RazorpayButton from "../components/RazorpayButton";

function FieldGroup({ label, children, fullWidth }) {
  return (
    <div style={{ display:"flex",flexDirection:"column",gap:"5px",gridColumn:fullWidth?"1/-1":"auto" }}>
      <label style={{ fontSize:"0.75rem",fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.06em" }}>{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, required, type="text" }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type} value={value} onChange={onChange}
      placeholder={placeholder} required={required}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={{
        padding:"10px 14px",borderRadius:"var(--radius)",
        border:`1px solid ${focused?"var(--input-focus)":"var(--input-border)"}`,
        background:"var(--input-bg)",color:"var(--ink)",
        fontSize:"0.88rem",fontFamily:"var(--font-body)",outline:"none",
        transition:"border-color 0.15s",width:"100%",
        boxShadow: focused ? "0 0 0 3px var(--gold-faint)" : "none",
      }}
    />
  );
}

export default function Cart() {
  const { cart, removeFromCart, updateQuantity, total } = useCart();
  const navigate = useNavigate();

  const [address, setAddress] = useState({ name:"",phone:"",line1:"",line2:"",city:"",state:"",pincode:"" });
  const set = (field) => (e) => setAddress((prev) => ({ ...prev, [field]: e.target.value }));

  const FREE_SHIPPING_IDS = [7];
  const allFreeShipping = cart.length > 0 && cart.every(
    i => i.product?.freeShipping || FREE_SHIPPING_IDS.includes(Number(i.product_id))
  );
  const shipping = allFreeShipping || total >= 99900 ? 0 : 9900;
  const grandTotal = total + shipping;
  const addressComplete = address.name && address.phone && address.line1 && address.city && address.state && address.pincode;

  if (cart.length === 0) {
    return (
      <div style={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"65vh",textAlign:"center",padding:"2rem" }}>
        <div style={{ width:"90px",height:"90px",borderRadius:"50%",background:"var(--gold-faint)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:"1.5rem" }}>
          <ShoppingBag size={40} color="var(--gold)" strokeWidth={1.5} />
        </div>
        <h2 style={{ fontFamily:"var(--font-display)",fontSize:"1.8rem",color:"var(--ink)",marginBottom:"0.5rem" }}>Your cart is empty</h2>
        <p style={{ color:"var(--muted)",marginBottom:"2rem",maxWidth:"320px" }}>You haven't added any books yet. Browse our store to find beautiful hardcopy editions.</p>
        <button onClick={() => navigate("/store")} style={{
          padding:"11px 28px",borderRadius:"var(--radius-full)",border:"none",
          background:"linear-gradient(135deg,var(--gold),var(--gold-2))",
          color:"#fff",fontWeight:700,fontSize:"0.92rem",cursor:"pointer",
          boxShadow:"var(--shadow-gold)",
        }}>Browse Store</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth:"var(--max-w)",margin:"0 auto",padding:"2.5rem 2rem 5rem" }}>

      {/* Header */}
      <div style={{ display:"flex",alignItems:"center",gap:"12px",marginBottom:"2rem" }}>
        <button onClick={() => navigate("/store")} style={{
          width:"36px",height:"36px",borderRadius:"var(--radius-full)",border:"1px solid var(--dust)",
          background:"none",display:"flex",alignItems:"center",justifyContent:"center",
          color:"var(--muted)",cursor:"pointer",transition:"all 0.15s",flexShrink:0,
        }}
          onMouseEnter={(e)=>{ e.currentTarget.style.borderColor="var(--gold)"; e.currentTarget.style.color="var(--gold)"; }}
          onMouseLeave={(e)=>{ e.currentTarget.style.borderColor="var(--dust)"; e.currentTarget.style.color="var(--muted)"; }}
        ><ArrowLeft size={15} /></button>
        <div>
          <h1 style={{ fontFamily:"var(--font-display)",fontSize:"2rem",fontWeight:700,color:"var(--ink)",lineHeight:1 }}>Shopping Cart</h1>
          <p style={{ fontSize:"0.82rem",color:"var(--muted)",marginTop:"3px" }}>{cart.length} item{cart.length!==1?"s":""}</p>
        </div>
      </div>

      <div className="cart-grid" style={{ display:"grid",gridTemplateColumns:"1fr 380px",gap:"2.5rem",alignItems:"start" }}>

        {/* ── LEFT: Items + Address ── */}
        <div style={{ display:"flex",flexDirection:"column",gap:"1.5rem" }}>

          {/* Cart Items */}
          <div style={{ background:"var(--card-bg)",border:"1px solid var(--card-border)",borderRadius:"var(--radius-lg)",overflow:"hidden",boxShadow:"var(--shadow)" }}>
            <div style={{ padding:"1rem 1.25rem",borderBottom:"1px solid var(--card-border)",display:"flex",alignItems:"center",gap:"8px" }}>
              <ShoppingBag size={15} color="var(--gold)" />
              <span style={{ fontWeight:700,fontSize:"0.88rem",color:"var(--ink)" }}>Your Items</span>
            </div>
            {cart.map((item, idx) => {
              const { product } = item;
              return (
                <div key={item.product_id} style={{
                  display:"flex",gap:"1rem",padding:"1.25rem",
                  borderBottom: idx < cart.length-1 ? "1px solid var(--card-border)" : "none",
                  alignItems:"flex-start",
                }}>
                  {/* Cover thumbnail */}
                  <div style={{
                    width:"72px",height:"100px",borderRadius:"var(--radius)",flexShrink:0,
                    background:`linear-gradient(155deg,#1C0F04,#5A2E0A)`,
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:"1.5rem",boxShadow:"var(--shadow)",overflow:"hidden",
                  }}>
                    {product.cover_url
                      ? <img src={product.cover_url} alt={product.title} style={{ width:"100%",height:"100%",objectFit:"cover" }} />
                      : "📖"
                    }
                  </div>

                  <div style={{ flex:1,minWidth:0 }}>
                    <h3 style={{ fontFamily:"var(--font-display)",fontSize:"1.1rem",fontWeight:700,color:"var(--ink)",lineHeight:1.25,marginBottom:"3px" }}>
                      {product.title}
                    </h3>
                    <p style={{ fontSize:"0.8rem",color:"var(--muted)",marginBottom:"10px" }}>{product.author}</p>

                    <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:"10px" }}>
                      {/* Qty control */}
                      <div style={{ display:"flex",alignItems:"center",gap:"8px",background:"var(--cream-2)",borderRadius:"var(--radius-full)",padding:"3px" }}>
                        <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} style={{
                          width:"26px",height:"26px",borderRadius:"50%",border:"none",
                          background: item.quantity<=1?"transparent":"var(--card-bg)",
                          color: item.quantity<=1?"var(--muted)":"var(--ink)",
                          display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",
                        }}>
                          <Minus size={11} />
                        </button>
                        <span style={{ fontSize:"0.88rem",fontWeight:700,color:"var(--ink)",minWidth:"20px",textAlign:"center" }}>{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} style={{
                          width:"26px",height:"26px",borderRadius:"50%",border:"none",
                          background:"var(--card-bg)",color:"var(--ink)",
                          display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",
                        }}>
                          <Plus size={11} />
                        </button>
                      </div>

                      <div style={{ display:"flex",alignItems:"center",gap:"12px" }}>
                        <span style={{ fontFamily:"var(--font-display)",fontSize:"1.25rem",fontWeight:700,color:"var(--ink)" }}>
                          ₹{((product.price * item.quantity)/100).toFixed(0)}
                        </span>
                        <button onClick={() => removeFromCart(item.product_id)} style={{
                          width:"30px",height:"30px",borderRadius:"50%",border:"1px solid var(--dust)",
                          background:"none",display:"flex",alignItems:"center",justifyContent:"center",
                          color:"var(--muted)",cursor:"pointer",transition:"all 0.15s",
                        }}
                          onMouseEnter={(e)=>{ e.currentTarget.style.background="var(--rust-faint)"; e.currentTarget.style.color="var(--rust)"; e.currentTarget.style.borderColor="var(--rust)"; }}
                          onMouseLeave={(e)=>{ e.currentTarget.style.background="none"; e.currentTarget.style.color="var(--muted)"; e.currentTarget.style.borderColor="var(--dust)"; }}
                        ><Trash2 size={13} /></button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Delivery Address */}
          <div style={{ background:"var(--card-bg)",border:"1px solid var(--card-border)",borderRadius:"var(--radius-lg)",overflow:"hidden",boxShadow:"var(--shadow)" }}>
            <div style={{ padding:"1rem 1.25rem",borderBottom:"1px solid var(--card-border)",display:"flex",alignItems:"center",gap:"8px" }}>
              <Truck size={15} color="var(--gold)" />
              <span style={{ fontWeight:700,fontSize:"0.88rem",color:"var(--ink)" }}>Delivery Address</span>
            </div>
            <div style={{ padding:"1.25rem",display:"grid",gridTemplateColumns:"1fr 1fr",gap:"1rem" }}>
              <FieldGroup label="Full Name" fullWidth><Input value={address.name} onChange={set("name")} placeholder="John Bunyan" required /></FieldGroup>
              <FieldGroup label="Phone"><Input value={address.phone} onChange={set("phone")} placeholder="+91 98765 43210" required type="tel" /></FieldGroup>
              <FieldGroup label="Email"><Input value={address.email||""} onChange={set("email")} placeholder="email@example.com" type="email" /></FieldGroup>
              <FieldGroup label="Address Line 1" fullWidth><Input value={address.line1} onChange={set("line1")} placeholder="House No, Street Name" required /></FieldGroup>
              <FieldGroup label="Address Line 2 (Optional)" fullWidth><Input value={address.line2} onChange={set("line2")} placeholder="Apartment, Suite, etc." /></FieldGroup>
              <FieldGroup label="City"><Input value={address.city} onChange={set("city")} placeholder="Chennai" required /></FieldGroup>
              <FieldGroup label="State"><Input value={address.state} onChange={set("state")} placeholder="Tamil Nadu" required /></FieldGroup>
              <FieldGroup label="Pincode"><Input value={address.pincode} onChange={set("pincode")} placeholder="600001" required /></FieldGroup>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Order Summary ── */}
        <div style={{ display:"flex",flexDirection:"column",gap:"1rem",position:"sticky",top:"calc(var(--nav-h) + 1.5rem)" }}>
          <div style={{ background:"var(--card-bg)",border:"1px solid var(--card-border)",borderRadius:"var(--radius-lg)",overflow:"hidden",boxShadow:"var(--shadow)" }}>
            <div style={{ padding:"1rem 1.25rem",borderBottom:"1px solid var(--card-border)",display:"flex",alignItems:"center",gap:"8px" }}>
              <Tag size={15} color="var(--gold)" />
              <span style={{ fontWeight:700,fontSize:"0.88rem",color:"var(--ink)" }}>Order Summary</span>
            </div>
            <div style={{ padding:"1.25rem",display:"flex",flexDirection:"column",gap:"12px" }}>
              {cart.map((item) => (
                <div key={item.product_id} style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"8px" }}>
                  <span style={{ fontSize:"0.82rem",color:"var(--muted)",flex:1,lineHeight:1.4 }}>
                    {item.product.title} × {item.quantity}
                  </span>
                  <span style={{ fontSize:"0.82rem",fontWeight:600,color:"var(--ink)",flexShrink:0 }}>
                    ₹{((item.product.price*item.quantity)/100).toFixed(0)}
                  </span>
                </div>
              ))}

              <div style={{ borderTop:"1px solid var(--card-border)",paddingTop:"12px",display:"flex",flexDirection:"column",gap:"8px" }}>
                <div style={{ display:"flex",justifyContent:"space-between",fontSize:"0.82rem",color:"var(--muted)" }}>
                  <span>Subtotal</span><span style={{ color:"var(--ink)" }}>₹{(total/100).toFixed(0)}</span>
                </div>
                <div style={{ display:"flex",justifyContent:"space-between",fontSize:"0.82rem",color:"var(--muted)" }}>
                  <span>Shipping</span>
                  <span style={{ color: shipping===0 ? "var(--sage)" : "var(--ink)" }}>{shipping===0?"FREE":"₹"+((shipping)/100).toFixed(0)}</span>
                </div>
                {shipping > 0 && (
                  <div style={{ fontSize:"0.72rem",color:"var(--muted)",background:"var(--gold-faint)",padding:"6px 10px",borderRadius:"var(--radius-sm)",display:"flex",alignItems:"center",gap:"6px" }}>
                    <Truck size={11} color="var(--gold)" />
                    Add ₹{((99900-total)/100).toFixed(0)} more for free shipping
                  </div>
                )}
              </div>

              <div style={{ borderTop:"2px solid var(--card-border)",paddingTop:"12px",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                <span style={{ fontWeight:700,color:"var(--ink)" }}>Total</span>
                <span style={{ fontFamily:"var(--font-display)",fontSize:"1.5rem",fontWeight:700,color:"var(--ink)" }}>
                  ₹{(grandTotal/100).toFixed(0)}
                </span>
              </div>

              <RazorpayButton
                amount={grandTotal}
                address={address}
                cart={cart}
                disabled={!addressComplete}
              />

              {!addressComplete && (
                <p style={{ fontSize:"0.72rem",color:"var(--muted)",textAlign:"center",lineHeight:1.5 }}>
                  Please fill in all required address fields to proceed.
                </p>
              )}

              <div style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:"6px",color:"var(--muted)",fontSize:"0.72rem" }}>
                <Lock size={11} />
                Secure checkout via Razorpay
              </div>
            </div>
          </div>

          {/* Trust badges */}
          <div style={{ background:"var(--card-bg)",border:"1px solid var(--card-border)",borderRadius:"var(--radius-lg)",padding:"1rem 1.25rem",display:"flex",gap:"1rem",flexWrap:"wrap",boxShadow:"var(--shadow)" }}>
            {[
              { icon:"🔒", label:"256-bit SSL" },
              { icon:"🚚", label:"Fast Shipping" },
              { icon:"↩️", label:"Easy Returns" },
            ].map(({ icon, label }) => (
              <div key={label} style={{ display:"flex",alignItems:"center",gap:"6px",fontSize:"0.72rem",color:"var(--muted)",fontWeight:500 }}>
                <span>{icon}</span>{label}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media(max-width:900px){
          .cart-grid { grid-template-columns:1fr !important; }
        }
      `}</style>
    </div>
  );
}
